import { gql } from '@apollo/client';
import Dropdown from '@orn/shared-web/components/Dropdown';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { CalendarIcon, EditIcon, EyeOffIcon } from '@orn/shared-web/icons';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '../../../paths';
import AccountMetadata from '../../AccountMetadata';
import { DateRangePicker, DateRangePickerModal, type CalendarDateRange } from '../../DateRangePicker';
import DeleteFinancialAccountModal from '../../DeleteFinancialAccountModal';
import Entity from '../../entity';
import PlaidFixConnectionButton from '../../plaid/PlaidFixConnectionButton';
import UpdateAccountModal from '../../UpdateAccountModal';

import type { AccountPaneHeaderFragment } from './__generated__/AccountPaneHeader.gql';

const AccountPaneHeaderFragment = gql`
  fragment AccountPaneHeader on FinancialAccount {
    id
    name
    subtype
    mask
    ...EntityAvatar
    bankConnection {
      id
      lastSyncedAt
      needsReconnection
    }
    bank {
      id
      name
    }
  }
  ${Entity.Avatar.Fragment}
`;

interface IAccountPaneHeaderProps {
  financialAccount: AccountPaneHeaderFragment;
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (dateRange: CalendarDateRange) => void;
}

/**
 * Pane header for a single financial account. Renders the account avatar, name,
 * inline metadata (subtype, bank, mask, sync status) and trailing actions
 * (date range, reconnect prompt, edit menu). The avatar is `md` (40px) at
 * every breakpoint so the title line matches its sibling pane headers. At
 * `<md` the sync status drops to its own line below the meta row to
 * match the Figma Account "Section Header" layout. The inline date range
 * picker is hidden at `<md` and remains accessible via the dots-menu.
 */
const AccountPaneHeader = ({ financialAccount, dateRange, onDateRangeChange }: IAccountPaneHeaderProps) => {
  const isMdOrUp = useBreakpoint('md');
  const navigate = useNavigate();
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = React.useState(false);

  const needsReconnection = financialAccount.bankConnection?.needsReconnection ?? false;

  return (
    <>
      <Entity.Header
        title={financialAccount.name}
        subtitle={
          <AccountMetadata
            subtype={financialAccount.subtype}
            bankName={financialAccount.bank.name}
            mask={financialAccount.mask}
            lastSyncedAt={financialAccount.bankConnection?.lastSyncedAt ?? undefined}
            syncStatusPlacement="stacked-below"
          />
        }
        avatar={<Entity.Avatar entity={financialAccount} size="md" />}
        trailingContent={
          <div className="flex items-center gap-3">
            {needsReconnection && (
              <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="sm" />
            )}
            {isMdOrUp && <DateRangePicker value={dateRange} onChange={onDateRangeChange} defaultValue="past6Months" />}
            <Dropdown.Root>
              <Dropdown.DotsButton className="p-2" />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu>
                  {!isMdOrUp && (
                    <Dropdown.Item icon={CalendarIcon} onAction={() => setIsDateRangeModalOpen(true)}>
                      Time range&hellip;
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item icon={EditIcon} onAction={() => setIsUpdateModalOpen(true)}>
                    Edit account&hellip;
                  </Dropdown.Item>
                  <Dropdown.Item icon={EyeOffIcon} destructive onAction={() => setIsDeleteModalOpen(true)}>
                    Hide account&hellip;
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        }
      />
      <UpdateAccountModal account={financialAccount} isOpen={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen} />
      <DeleteFinancialAccountModal
        account={financialAccount}
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onDeleted={() => navigate(PATHS.ACCOUNTS)}
      />
      {!isMdOrUp && (
        <DateRangePickerModal
          isOpen={isDateRangeModalOpen}
          onOpenChange={setIsDateRangeModalOpen}
          value={dateRange}
          onChange={onDateRangeChange}
          defaultValue="past6Months"
        />
      )}
    </>
  );
};

AccountPaneHeader.Fragment = AccountPaneHeaderFragment;

export default AccountPaneHeader;
